import { useDemoScenarios } from "@/hooks/useDemoScenarios";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { ClinicalCodeWithOrder, DemoScenarioWithFormattedCodes } from "@/types/demo";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router";
import { Pencil } from "lucide-react";

export function DemoScenarioTable() {
  const { scenarios, isLoading, deleteScenario } = useDemoScenarios();
  const navigate = useNavigate();

  const handleDelete = async (id: string) => {
    try {
      await deleteScenario.mutateAsync(id);
      toast.success("Demo scenario deleted successfully");
    } catch (_error) {
      toast.error("Failed to delete demo scenario");
    }
  };

  const handleEdit = (scenario: DemoScenarioWithFormattedCodes) => {
    // Store the scenario in sessionStorage for the edit page to access
    sessionStorage.setItem("editScenario", JSON.stringify(scenario));
    navigate(`/admin/edit-demo/${scenario.id}`);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Demo Text</TableHead>
            <TableHead>Clinical Codes</TableHead>
            <TableHead className="w-[180px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {scenarios?.map((scenario) => (
            <TableRow key={scenario.id}>
              <TableCell className="font-medium max-w-md">
                {/* Use white-space: pre-line to preserve line breaks */}
                <div className="whitespace-pre-line">{scenario.demo_text}</div>
              </TableCell>
              <TableCell>
                <div className="space-y-1">
                  {/* Use type assertion to safely convert clinical_codes to ClinicalCodeWithOrder[] */}
                  {(scenario.clinical_codes as unknown as ClinicalCodeWithOrder[])
                    .sort((a, b) => a.order - b.order)
                    .map((code) => (
                      <div key={code.code} className="text-sm flex items-center gap-2">
                        <span className="font-medium">{code.code}</span>
                        <span className="text-muted-foreground">{code.description}</span>
                      </div>
                    ))}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Properly type-cast the scenario before passing to handleEdit
                      const formattedScenario = {
                        ...scenario,
                        clinical_codes: scenario.clinical_codes as unknown as ClinicalCodeWithOrder[],
                      } as DemoScenarioWithFormattedCodes;

                      handleEdit(formattedScenario);
                    }}
                  >
                    <Pencil className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(scenario.id)}>
                    Delete
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
